import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import classNames from 'classnames';
import { FNIType, STATES } from '@upstart/harmony/lib/legos/constants';
import { LendingStatus } from '../../../utils/constants';
import { Button } from '../../Input/Button/Button';
import { DealerLaunchStepProps } from '../types';
// eslint-disable-next-line import/no-cycle
import { getActiveDealerLaunchSections } from '../steps';
// eslint-disable-next-line import/no-cycle
import { Checkbox } from '../../Input/Checkbox/Checkbox';
import styles from '../styles.module.scss';
import { logout } from '../../../utils/redux';
import { convertFeeTableToDealerFees } from '../../../utils/conversions';
import { useAppDispatch, useTypedSelector } from '../../../redux/store';
import { getLendingStatus, submitLendingData } from '../../../utils/lending';

export const DealerLaunchReview: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  saveDealer,
}) => {
  const dispatch = useAppDispatch();
  const globalConfigurationStore = useTypedSelector(
    (state) => state.globalConfigurations,
  );
  const { globalConfigurations } = globalConfigurationStore;
  const [isModalOpen, setModalOpen] = useState(false);
  const [confirmedSubmit, setConfirmedSubmit] = useState(false);
  const lendingStatus = getLendingStatus(dealer, globalConfigurations);
  const fniType = dealer.fniType as FNIType;

  useEffect(() => {
    async function callSetupLendingEnabledDealer() {
      await submitLendingData(dealer, globalConfigurations);
    }
    callSetupLendingEnabledDealer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reset confirmedSubmit when modal closes
  useEffect(() => {
    if (!isModalOpen) {
      setConfirmedSubmit(false);
    }
  }, [isModalOpen]);

  const sections = useMemo(() => getActiveDealerLaunchSections(dealer), [
    dealer,
  ]);
  const hasLaunchBlocker = useMemo(
    () =>
      !!sections.find((section) =>
        section.steps.find(
          (step) => step.isLaunchBlocker && !step.isComplete(dealer),
        ),
      ),
    [sections, dealer],
  );

  const onSubmit = () => {
    let fees;
    fees = { ...dealer.fees };
    // If fees was not set, set in empty fees
    if (Object.keys(fees).length < 1) {
      // Convert empty fees to the structure that dealerFees is compatible with
      const defaultFees = convertFeeTableToDealerFees({
        cash: [],
        lease: [],
        purchase: [],
      });
      fees = defaultFees;
    }
    saveDealer({
      launchpad: {
        ...dealer.launchpad,
        status: { ...dealer.launchpad?.status, readyToLaunch: true },
      },
      fees,
    });
  };
  const isBeforePricingValidation = !dealer.launchpad?.status
    ?.implementerVerifiedInventory;

  const renderBody = () => (
    <div className={styles.beforePricingValidationContainer}>
      <h1>Next steps</h1>
      <p>
        We can&apos;t wait to launch your business on Upstart Auto Retail and
        optimize your sales experience. Next, you have a few simple steps to
        complete at a later time.&#160;
        <b>Don&apos;t worry -- we&apos;ll walk you through these next steps.</b>
      </p>
      <h2>Validate Pricing</h2>
      <p>
        After we verify your inventory, you will receive an email{' '}
        {dealer.launchpad?.email && (
          <>
            sent to <b>{dealer.launchpad.email}</b>{' '}
          </>
        )}
        with a link to our pricing validation tool. Follow the steps to verify
        pricing details for a vehicle from your inventory to ensure we have
        accurate prices.
      </p>
      {fniType === FNIType.DEALERTRACK && (
        <>
          <h2>Enable Lending</h2>
          <p>
            You must log in to your F&#38;I system Dealertrack and complete a
            few simple steps to enable Upstart Auto Retail Lending. Download the
            setup guide below and save it to your computer so you can follow the
            steps.
            <br />
            <Button
              label="Download Dealertrack Setup Guide"
              variant="minimal"
              onClick={() =>
                window.open(
                  'https://prodigy-cdn.upstart.com/launchpad/FNI/Dealertrack+2021+Finance+Services+Set-up+Guide.pdf',
                  '_blank',
                )
              }
            />
          </p>
        </>
      )}
      {fniType === FNIType.ROUTEONE && (
        <>
          <h2>Enable Lending</h2>
          <p>
            You must log in to your F&#38;I system RouteOne and complete a few
            simple steps to enable Upstart Auto Retail Lending. Download the
            setup guide below and save it to your computer so you can follow the
            steps.
            <br />
            <Button
              label="Download RouteOne Setup Guide"
              variant="minimal"
              onClick={() =>
                window.open(
                  'https://prodigy-cdn.upstart.com/launchpad/FNI/RouteOne+ORS+Setup+Guide.pptx',
                  '_blank',
                )
              }
            />
          </p>
        </>
      )}
      {lendingStatus === LendingStatus.StateDisabled && (
        <p>{`We're launching lending in more states each month and will let you know soon when it's available in ${
          STATES[dealer.state as keyof typeof STATES] || 'your state'
        }.`}</p>
      )}
      <h2>All done!</h2>
      <p>
        There&apos;s nothing more for you to do here in Launch Pad. We&apos;ve
        saved your info and will email you any next steps. In the meantime, you
        can edit the information you entered or log out.
      </p>
    </div>
  );

  const continueLabel = isBeforePricingValidation
    ? 'Save and Logout'
    : 'Continue';

  return (
    <div className={classNames(styles.stepContainer, styles.reviewStep)}>
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setModalOpen(false)}
        contentLabel="Are you sure"
        className={styles.reviewModal}
      >
        <div className={styles.modalTitle}>
          Are You Sure You Want To Submit Your Setup Details?
        </div>
        {sections.find(
          (section) =>
            !section.hideInReviewSummary && !section.isComplete(dealer),
        ) ? (
          <>
            <div className={styles.modalDescription}>
              We noticed that you have a few sections with incomplete
              information. We highly recommend filling in the following missing
              steps so that our team can get you started sooner:
            </div>
            <div className={styles.modalIncompleteSections}>
              {sections
                .filter(
                  (section) =>
                    !section.hideInReviewSummary && !section.isComplete(dealer),
                )
                .map((section, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={index}>
                    <div className={styles.modalIncompleteSectionName}>
                      {section.name}
                    </div>
                    {section.steps
                      .filter((step) => !step.isComplete(dealer))
                      .map((step, subIndex) => (
                        <div
                          // eslint-disable-next-line react/no-array-index-key
                          key={subIndex}
                          className={classNames(
                            styles.modalIncompleteStepName,
                            step.isLaunchBlocker && styles.modalSevere,
                          )}
                        >
                          {step.name}
                        </div>
                      ))}
                  </div>
                ))}
            </div>
          </>
        ) : null}

        <div className={styles.modalDescription}>
          If you have filled in all the information that you can, please confirm
          and our team will follow up with you. Once you submit your
          information, you will not be able to go back and make changes.
        </div>
        <div className={styles.modalWarning}>
          Please note: Submitting incorrect, or missing information may
          significantly delay your product integration.
        </div>
        {hasLaunchBlocker && (
          <div className={styles.modalSevere}>
            Steps highlighted in red are required to launch, submission is
            disabled until they are complete.
          </div>
        )}

        <Checkbox
          label="I have filled in as much information as I can and am ready to proceed"
          className={styles.modalCheckbox}
          value={confirmedSubmit}
          onChange={setConfirmedSubmit}
        />

        <div className={styles.pageNav}>
          <Button
            className={styles.pageNavButton}
            label="Return to Review"
            onClick={() => setModalOpen(false)}
          />
          <div className="spacer" />
          <Button
            className={styles.pageNavButton}
            label="Submit Setup Details"
            variant="primary"
            disabled={hasLaunchBlocker || !confirmedSubmit}
            onClick={onSubmit}
          />
        </div>
      </Modal>
      <div className={styles.stepTitle}>
        You completed the first part of your new account setup
      </div>
      {!isBeforePricingValidation && (
        <div className={styles.stepSubtitle}>
          Congratulations on finishing the setup! Once you hit the final submit
          button, a member of our team will get verify your settings and get in
          touch with you when you&apos;re ready to launch.
        </div>
      )}
      <div className={styles.stepCard}>{renderBody()}</div>
      <div className={styles.pageNav}>
        <Button
          className={styles.pageNavButton}
          label="Previous"
          onClick={prevStep}
        />
        <div className="spacer" />
        <Button
          className={styles.pageNavButton}
          label={continueLabel}
          variant="primary"
          onClick={() => {
            if (isBeforePricingValidation) {
              saveDealer({
                launchpad: {
                  ...dealer.launchpad,
                  status: {
                    ...dealer.launchpad?.status,
                    readyToBeginInventoryConfig: true,
                  },
                },
              });
              logout(dispatch);
            } else {
              setModalOpen(true);
            }
          }}
        />
      </div>
    </div>
  );
};
